import { Button, Chip, Select, SelectItem, Textarea } from '@heroui/react';
import { Sparkles, Upload, X } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../../hooks';
import {
  AiAssetInput,
  AiLayoutHint,
  PaletteMode,
  useAdGeneratorSettings,
  useGenerateAdCreative,
  useImportStockPhoto,
} from '../../../../../queries';
import { findFormat, findPlatform } from '../../../config/platforms';
import { TEMPLATES } from '../../../config/templates';
import { useAssetUpload } from '../../../hooks';
import { useAdsGeneratorStore } from '../../../model/store';
import { AdElement } from '../../../model/types';
import { readBackground, readElements } from '../../../utils/scene';
import {
  ComposerBody,
  ComposerHeader,
  ComposerHint,
  ComposerOverlay,
  ComposerTitle,
  FieldGroup,
  FieldTitle,
  ReferenceRow,
} from './AiComposer.styled';
import { AiProgress } from './AiProgress';
import { AI_SCENARIOS } from './aiScenarios';
import { AssetPicker } from './AssetPicker';
import { CopyAssistant } from './CopyAssistant';

interface AiComposerProps {
  onClose: () => void;
}

const PALETTE_OPTIONS: { key: PaletteMode; label: string }[] = [
  { key: 'brand', label: 'Paleta marki Hotelspot' },
  { key: 'reference', label: 'Kolory ze wzoru kompozycji' },
  { key: 'free', label: 'Dobierz swobodnie' },
];

const PLACEHOLDER = 'Co chcesz wygenerować?';

/** Zdjęcie zajmujące większość kadru pobieramy w większym rozmiarze. */
const isBackgroundSized = (element: AdElement, sceneArea: number): boolean => element.width * element.height >= sceneArea * 0.7;

/**
 * Krok „stwórz kreację z AI" w miejscu planszy.
 *
 * Plansza zostaje zamontowana pod nakładką, więc eksport i skróty w górnym
 * pasku działają bez zmian. Wynik generacji zastępuje treść planszy i od razu
 * nadaje się do ręcznych poprawek.
 */
export const AiComposer: FC<AiComposerProps> = ({ onClose }) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const replaceElements = useAdsGeneratorStore((state) => state.replaceElements);
  const { showError, showSuccess, showWarning } = useHeroToast();
  const { mutateAsync: generate, isPending } = useGenerateAdCreative();
  const { mutateAsync: importPhoto } = useImportStockPhoto();
  const { brandColors } = useAdGeneratorSettings();
  const reference = useAssetUpload();

  const [prompt, setPrompt] = useState('');
  const [paletteMode, setPaletteMode] = useState<PaletteMode>('brand');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [assets, setAssets] = useState<AiAssetInput[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  if (!scene) return null;

  // Scenariusz rozpoznajemy po treści pola: własna zmiana opisu sama zdejmuje
  // zaznaczenie, bo to już nie jest ten gotowy scenariusz.
  const activeScenario = AI_SCENARIOS.find((scenario) => scenario.prompt === prompt);
  const platform = findPlatform(scene.platformId);
  const format = findFormat(scene.platformId, scene.formatId);
  const sceneArea = scene.width * scene.height;

  /**
   * Podmienia podglądy ze stocka na licencjonowane pliki. Pobieramy WYŁĄCZNIE
   * te zdjęcia, które model faktycznie wstawił, i po kolei, żeby dzienny licznik
   * puli rósł przewidywalnie, a ewentualna odmowa dotyczyła jednej pozycji.
   */
  const resolveStockImages = async (elements: AdElement[]): Promise<AdElement[]> => {
    const stockByUrl = new Map(assets.filter((asset) => asset.stockId).map((asset) => [asset.url, asset]));
    const usedUrls = Array.from(new Set(
      elements
        .filter((element): element is AdElement & { src: string } => element.type === 'image')
        .map((element) => element.src)
        .filter((url) => stockByUrl.has(url)),
    ));

    if (usedUrls.length === 0) return elements;

    setIsImporting(true);
    let resolved = elements;
    const failed: string[] = [];

    try {
      // Sekwencyjnie, nie równolegle: dzienny licznik puli ma rosnąć
      // przewidywalnie, a odmowa dotyczyć jednej pozycji, nie całej paczki.
      // eslint-disable-next-line no-restricted-syntax
      for (const url of usedUrls) {
        const asset = stockByUrl.get(url);
        const placed = resolved.find((element) => element.type === 'image' && element.src === url);

        try {
          // eslint-disable-next-line no-await-in-loop
          const imported = await importPhoto({
            id: asset?.stockId as number,
            usage: placed && isBackgroundSized(placed, sceneArea) ? 'background' : 'element',
            canvasLongEdge: Math.max(scene.width, scene.height),
          });
          resolved = resolved.map((element) => (
            element.type === 'image' && element.src === url
              ? { ...element, src: imported.asset.url }
              : element
          ));
        } catch {
          failed.push(url);
        }
      }
    } finally {
      setIsImporting(false);
    }

    if (failed.length > 0) {
      // Podgląd ze stocka jest znakowany i nielicencjonowany, więc nieudanego
      // pobrania nie wolno zostawić na planszy.
      resolved = resolved.filter((element) => !(element.type === 'image' && failed.includes(element.src)));
      showWarning(`Nie udało się pobrać ${failed.length} zdjęć ze stocka. Kreacja powstała bez nich.`);
    }

    return resolved;
  };

  /**
   * Układ wskazanego szablonu jako wzór dla modelu. Wysyłamy PROCENTY planszy,
   * a nie piksele: ten sam szablon ma działać na kwadracie i na relacji 9:16,
   * a przeliczenie na piksele robi już backend, znając wymiary.
   */
  const buildLayout = (): AiLayoutHint | undefined => {
    const template = TEMPLATES.find((item) => item.id === templateId);
    if (!template) return undefined;

    const result = template.build(scene.width, scene.height);
    return {
      label: template.label,
      elements: result.elements.map((element) => ({
        type: element.type,
        name: element.name,
        x: Math.round((element.x / scene.width) * 1000) / 10,
        y: Math.round((element.y / scene.height) * 1000) / 10,
        width: Math.round((element.width / scene.width) * 1000) / 10,
        height: Math.round((element.height / scene.height) * 1000) / 10,
      })),
    };
  };

  const submit = async (): Promise<void> => {
    try {
      const result = await generate({
        prompt: prompt.trim(),
        referenceImageUrl: referenceUrl || undefined,
        platformLabel: platform?.name,
        formatLabel: format?.label,
        width: scene.width,
        height: scene.height,
        safeArea: format?.safeArea,
        paletteMode,
        assets,
        brandColors,
        layout: buildLayout(),
      });

      const elements = await resolveStockImages(readElements(result.scene?.elements ?? []));
      replaceElements(elements, readBackground(result.scene?.background));

      showSuccess(result.summary || 'Układ gotowy, popraw go na planszy');
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się wygenerować układu');
    }
  };

  const isBusy = isPending || isImporting;

  return (
    <ComposerOverlay>
      <ComposerHeader>
        <div>
          <ComposerTitle>Kreacja z AI</ComposerTitle>
          <ComposerHint>
            {`Plansza ${scene.width} x ${scene.height} px. Wynik zastąpi bieżącą treść, cofniesz go skrótem Ctrl+Z.`}
          </ComposerHint>
        </div>
        <Button
          size="sm"
          radius="sm"
          isIconOnly
          variant="light"
          aria-label="Zamknij"
          isDisabled={isBusy}
          onPress={onClose}
        >
          <X size={16} />
        </Button>
      </ComposerHeader>

      <ComposerBody>
        <CopyAssistant />

        <div className="flex flex-col gap-2">
          <Textarea
            size="sm"
            radius="sm"
            minRows={4}
            label="Co ma być na kreacji?"
            labelPlacement="outside"
            placeholder={PLACEHOLDER}
            value={prompt}
            maxLength={1500}
            onValueChange={setPrompt}
          />

          <div className="flex flex-wrap gap-1">
            {AI_SCENARIOS.map((scenario) => (
              <Chip
                key={scenario.label}
                size="sm"
                radius="sm"
                variant={activeScenario?.label === scenario.label ? 'solid' : 'flat'}
                color={activeScenario?.label === scenario.label ? 'primary' : 'default'}
                className="cursor-pointer text-xs"
                onClick={() => setPrompt(scenario.prompt)}
              >
                {scenario.label}
              </Chip>
            ))}
          </div>
          <ComposerHint>
            Przykładowe scenariusze wstawiają gotowy opis do pola. Możesz go potem dowolnie przepisać.
          </ComposerHint>
        </div>

        <AssetPicker selected={assets} onChange={setAssets} stockTerm={activeScenario?.stockTerm} />

        <FieldGroup>
          <FieldTitle>Wzór kompozycji (opcjonalny)</FieldTitle>
          <ComposerHint>
            Z tego pliku AI weźmie sam układ i nastrój. Na planszy nie wyląduje.
          </ComposerHint>
          <input
            ref={reference.inputRef}
            type="file"
            accept={reference.accept}
            hidden
            onChange={async (event) => {
              const uploaded = await reference.handleFiles(event.target.files);
              if (uploaded[0]) setReferenceUrl(uploaded[0].url);
            }}
          />
          <ReferenceRow>
            {Boolean(referenceUrl) && <img src={referenceUrl} alt="" />}
            <Button
              size="sm"
              radius="sm"
              variant="flat"
              startContent={<Upload size={16} />}
              isLoading={reference.isUploading}
              onPress={reference.openPicker}
            >
              {referenceUrl ? 'Zmień wzór' : 'Wgraj wzór'}
            </Button>
            {Boolean(referenceUrl) && (
              <Button size="sm" radius="sm" variant="light" onPress={() => setReferenceUrl('')}>
                Bez wzoru
              </Button>
            )}
          </ReferenceRow>
        </FieldGroup>

        <Select
          size="sm"
          radius="sm"
          label="Układ z szablonu"
          labelPlacement="outside"
          aria-label="Układ z szablonu"
          description="AI ułoży bloki tak jak w wybranym szablonie, ale napisze własną treść."
          selectedKeys={new Set([templateId || 'none'])}
          onSelectionChange={(keys) => {
            const [next] = Array.from(keys as Set<string>);
            setTemplateId(next === 'none' ? '' : String(next ?? ''));
          }}
        >
          <>
            <SelectItem key="none" textValue="Bez szablonu">
              Bez szablonu, AI układa po swojemu
            </SelectItem>
            {TEMPLATES.filter((template) => template.id !== 'blank').map((template) => (
              <SelectItem key={template.id} textValue={template.label}>
                {template.label}
              </SelectItem>
            ))}
          </>
        </Select>

        <Select
          size="sm"
          radius="sm"
          label="Kolorystyka"
          labelPlacement="outside"
          aria-label="Kolorystyka"
          selectedKeys={new Set([paletteMode])}
          onSelectionChange={(keys) => {
            const [next] = Array.from(keys as Set<string>);
            if (next) setPaletteMode(next as PaletteMode);
          }}
        >
          {PALETTE_OPTIONS.map((option) => (
            <SelectItem key={option.key} textValue={option.label}>
              {option.label}
            </SelectItem>
          ))}
        </Select>

        {isBusy ? (
          <AiProgress isImporting={isImporting} />
        ) : (
          <div className="flex justify-start gap-2">
            <Button
              radius="sm"
              className="bg-black text-white"
              startContent={<Sparkles size={16} />}
              isDisabled={!prompt.trim() && !referenceUrl && assets.length === 0}
              onPress={submit}
            >
              Rozpocznij generowanie kreacji
            </Button>
            <Button radius="sm" variant="light" onPress={onClose}>
              Anuluj
            </Button>
          </div>
        )}
      </ComposerBody>
    </ComposerOverlay>
  );
};
